import { showNotification } from '@mantine/notifications';

import { getFreshAccessToken } from '../generated/baseApi';

interface DownloadButtonProps {
  experimentId: string;
  setIsLoading?: (isLoading: boolean) => void;
  filePath: string;
}

export const fetchFile = async ({ experimentId, filePath }: DownloadButtonProps) => {
  try {
    // Construct the URL with the definition query parameter
    const requestUrl = new URL(`/api/stellar/experiments/${experimentId}/download`, window.location.origin);
    requestUrl.searchParams.append('file_path', filePath);

    // Get a fresh JWT for authentication (auto-refreshes an expired session).
    const token = await getFreshAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add Authorization header if token is available
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(requestUrl, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      showNotification({
        title: 'Download failed',
        message: 'The download of the excel file failed. Please try again.',
        color: 'red',
      });
      throw new Error(`Failed to download the file: ${response.statusText}`);
    }
    const blob = await response.blob();

    if (!blob) {
      // show notification
      showNotification({
        title: 'Download failed',
        message: 'The download of the excel file failed. Please try again.',
        color: 'green',
      });
      throw new Error('Failed to download the file: Blob is empty');
    }
    return blob;
  } catch (error) {
    console.error('Error in download:', error);
    showNotification({
      title: 'Download failed',
      message: 'The download of the file failed. Please try again.',
      color: 'red',
    });
    throw new Error('Error in download');
  }
};

export const downloadFile = async ({ experimentId, setIsLoading, filePath }: DownloadButtonProps) => {
  setIsLoading(true);
  try {
    // Construct the URL with the definition query parameter
    const requestUrl = new URL(`/api/stellar/experiments/${experimentId}/download`, window.location.origin);
    requestUrl.searchParams.append('file_path', filePath);

    // Get a fresh JWT for authentication (auto-refreshes an expired session).
    const token = await getFreshAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add Authorization header if token is available
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(requestUrl, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      showNotification({
        title: 'Download failed',
        message: 'The download of the excel file failed. Please try again.',
        color: 'red',
      });
      throw new Error(`Failed to download the file: ${response.statusText}`);
    }
    const blob = await response.blob();

    if (!blob) {
      // show notification
      showNotification({
        title: 'Download failed',
        message: 'The download of the excel file failed. Please try again.',
        color: 'green',
      });
      throw new Error('Failed to download the file: Blob is empty');
    }

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    // extract file name from path
    const fileName = filePath.split('/').pop();
    a.download = fileName || 'downloaded_file';
    document.body.appendChild(a);
    a.click();

    // Cleanup
    a.remove();
    window.URL.revokeObjectURL(url);

    // show notification
    showNotification({
      title: 'Download successful',
      message: 'The file has been downloaded successfully. Please check your downloads folder.',
      color: 'green',
    });
  } catch (error) {
    console.error('Error in download:', error);
    showNotification({
      title: 'Download failed',
      message: 'The download of the file failed. Please try again.',
      color: 'red',
    });
    throw new Error('Error in download');
  } finally {
    setIsLoading(false); // Stop loading spinner
  }
};
